import { Button, buttonVariants } from '@xhubio-saas/core-frontend/ui-components'
import { FileUp, RefreshCw } from 'lucide-react'
import { type DragEvent, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { type ExtractFailure, PDF_MAX_BYTES } from '@/api/pdfExtractClient'
import type { SellerProfile, SellerRequirement } from '@/api/sellerProfileClient'

export interface PickedPdf {
  name: string
  size: number
  /** Base64 ohne `data:`-Vorspann. */
  content: string
}

async function toBase64(file: File): Promise<string> {
  const buffer = new Uint8Array(await file.arrayBuffer())
  let raw = ''
  for (let i = 0; i < buffer.length; i += 0x8000) {
    raw += String.fromCharCode(...buffer.subarray(i, i + 0x8000))
  }
  return btoa(raw)
}

/** Die Ablehnung beim Auslesen als ein Satz der Seite — ein Satz je Art (Entscheidungstabelle X2–X10). */
function FailureLine({ failure }: { failure: ExtractFailure }) {
  const { t } = useTranslation()
  if (failure.kind === 'quota') {
    return (
      <div
        className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-lg border border-destructive/40 p-3"
        role="alert"
        data-testid="invoice-from-pdf-quota-exceeded"
      >
        <p className="min-w-0 text-sm">
          <span className="font-medium text-destructive">
            {t('invoiceFromPdf.quotaExceeded.title')}
          </span>{' '}
          <span className="text-muted-foreground tabular-nums">
            {t('invoiceFromPdf.quotaExceeded.body', {
              used: failure.quota.current,
              limit: failure.quota.limit,
            })}
          </span>
        </p>
        <Link
          to="/subscription"
          className={buttonVariants({ variant: 'outline', size: 'sm', className: 'ml-auto' })}
        >
          {t('invoiceFromPdf.quotaExceeded.cta')}
        </Link>
      </div>
    )
  }
  const message =
    failure.kind === 'failed' && failure.message
      ? failure.message
      : t(`invoiceFromPdf.errors.${failure.kind}`)
  return (
    <p
      className="rounded-lg border border-destructive/40 p-3 text-sm text-destructive"
      role="alert"
      data-testid={`invoice-from-pdf-error-${failure.kind}`}
    >
      {message}
    </p>
  )
}

export interface UploadStepProps {
  access: 'allowed' | 'upgrade'
  seller: SellerProfile | null
  sellerMissing: SellerRequirement[]
  pdf: PickedPdf | null
  onPdfChange: (pdf: PickedPdf | null) => void
  onExtract: () => void
  running: boolean
  failure: ExtractFailure | null
  /** E19: Das PDF hat Text, aber weder Nummer noch Datum noch Betrag; es zählte nicht. Weiter geht es nur auf Wunsch. */
  notRecognized: boolean
  onFillManually: () => void
  /** Ohne Sitzungsschluessel sperrt der Knopf und nennt den Grund (wie die Konvertierung). */
  sessionKey: 'da' | 'laedt' | 'fehlt'
}

export function UploadStep({
  access,
  seller,
  sellerMissing,
  pdf,
  onPdfChange,
  onExtract,
  running,
  failure,
  notRecognized,
  onFillManually,
  sessionKey,
}: UploadStepProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const [rejected, setRejected] = useState<'type' | 'size' | null>(null)

  const read = async (file: File) => {
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    if (!isPdf) {
      setRejected('type')
      return
    }
    if (file.size > PDF_MAX_BYTES) {
      setRejected('size')
      return
    }
    setRejected(null)
    onPdfChange({ name: file.name, size: file.size, content: await toBase64(file) })
  }

  const handlers = {
    onDragOver: (e: DragEvent<HTMLElement>) => {
      e.preventDefault()
      setDragOver(true)
    },
    onDragLeave: () => setDragOver(false),
    onDrop: (e: DragEvent<HTMLElement>) => {
      e.preventDefault()
      setDragOver(false)
      const file = e.dataTransfer.files?.[0]
      if (file) void read(file)
    },
  }

  // E7: Free sieht statt der Ablage den Weg zum Upgrade.
  if (access === 'upgrade') {
    return (
      <section
        className="space-y-3 rounded-xl border bg-card p-6 shadow-sm"
        data-testid="invoice-from-pdf-upgrade"
      >
        <h2 className="text-base font-semibold">{t('invoiceFromPdf.upgrade.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('invoiceFromPdf.upgrade.body')}</p>
        <div className="flex justify-end">
          <Link to="/subscription" className={buttonVariants({ variant: 'default' })}>
            {t('invoiceFromPdf.upgrade.cta')}
          </Link>
        </div>
      </section>
    )
  }

  // E15: ohne vollstaendige Firmendaten erst der Hinweis mit Link, keine Ablage.
  if (sellerMissing.length > 0) {
    return (
      <section
        className="space-y-3 rounded-xl border bg-card p-6 shadow-sm"
        data-testid="invoice-from-pdf-profile-missing"
      >
        <h2 className="text-base font-semibold">{t('invoiceFromPdf.profileMissing.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('invoiceFromPdf.profileMissing.body')}</p>
        <ul className="list-inside list-disc text-sm" data-testid="invoice-from-pdf-profile-gaps">
          {sellerMissing.map((m) => (
            <li key={m}>{t(`sellerProfile.requirement.${m}`)}</li>
          ))}
        </ul>
        <div className="flex justify-end">
          <Link to="/settings/company-data" className={buttonVariants({ variant: 'default' })}>
            {t('invoiceFromPdf.profileMissing.cta')}
          </Link>
        </div>
      </section>
    )
  }

  const taxLine = seller?.vatId || seller?.taxId || ''
  const iban = seller?.bankAccount?.iban?.replace(/\s+/g, '') ?? ''
  const sessionHint =
    sessionKey === 'laedt'
      ? t('invoiceFromPdf.upload.sessionLoading')
      : sessionKey === 'fehlt'
        ? t('invoiceFromPdf.upload.sessionMissing')
        : null
  const canExtract = pdf !== null && !running && sessionKey === 'da'

  return (
    <section className="space-y-4 rounded-xl border bg-card p-6 shadow-sm">
      {pdf ? (
        <div
          data-testid="invoice-from-pdf-file"
          className={`flex w-full flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border px-4 py-2 ${
            dragOver ? 'border-primary bg-primary/5' : 'border-border'
          }`}
          {...handlers}
        >
          <FileUp className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="min-w-32 flex-1 truncate text-sm tabular-nums">
            {t('invoiceFromPdf.upload.fileLine', {
              name: pdf.name,
              kb: Math.max(1, Math.round(pdf.size / 1024)),
            })}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="ml-auto shrink-0 whitespace-nowrap"
            onClick={() => inputRef.current?.click()}
          >
            {t('invoiceFromPdf.upload.otherFile')}
          </Button>
        </div>
      ) : (
        <button
          type="button"
          data-testid="invoice-from-pdf-drop"
          className={`flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-10 text-center transition-[background-color,border-color] motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-ring ${
            dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 bg-card'
          }`}
          onClick={() => inputRef.current?.click()}
          {...handlers}
        >
          <FileUp className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
          <span className="font-medium">{t('invoiceFromPdf.upload.dropTitle')}</span>
          <span className="text-sm text-muted-foreground">
            {t('invoiceFromPdf.upload.dropHint')}
          </span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="hidden"
        data-testid="invoice-from-pdf-input"
        onChange={(e) => {
          const file = e.target.files?.[0]
          e.target.value = ''
          if (file) void read(file)
        }}
      />
      {rejected && (
        <p className="text-sm text-destructive" role="alert">
          {t(`invoiceFromPdf.upload.rejected.${rejected}`)}
        </p>
      )}

      <div
        className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-muted/50 px-3 py-2 text-sm"
        data-testid="invoice-from-pdf-seller-line"
      >
        <span className="min-w-0 [overflow-wrap:anywhere]">
          {t('invoiceFromPdf.upload.sellerLine', {
            name: seller?.name ?? '',
            taxId: taxLine,
            ibanEnd: iban.slice(-4),
          })}
        </span>
        <Link to="/settings/company-data" className="ml-auto text-sm underline underline-offset-4">
          {t('invoiceFromPdf.upload.editSeller')}
        </Link>
      </div>
      <p className="rounded-lg border border-amber-300/60 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-100">
        {t('invoiceFromPdf.upload.scanNote')}
      </p>

      {failure && <FailureLine failure={failure} />}

      {notRecognized && (
        <div
          className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-lg border border-amber-300/60 bg-amber-50 px-3 py-2 dark:border-amber-500/40 dark:bg-amber-950/40"
          role="alert"
          data-testid="invoice-from-pdf-not-recognized"
        >
          <p className="min-w-0 text-sm text-amber-900 dark:text-amber-100">
            {t('invoiceFromPdf.upload.notRecognized')}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="ml-auto"
            data-testid="invoice-from-pdf-fill-manually"
            onClick={onFillManually}
          >
            {t('invoiceFromPdf.upload.fillManually')}
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
        {sessionHint && (
          <p id="invoice-from-pdf-session" className="mr-auto text-sm text-muted-foreground">
            {sessionHint}
          </p>
        )}
        <Button
          onClick={onExtract}
          disabled={!canExtract}
          aria-describedby={sessionHint ? 'invoice-from-pdf-session' : undefined}
          data-testid="invoice-from-pdf-extract"
        >
          {running ? (
            <>
              <RefreshCw
                className="h-4 w-4 animate-spin motion-reduce:animate-none"
                aria-hidden="true"
              />
              {t('invoiceFromPdf.upload.extracting')}
            </>
          ) : (
            t('invoiceFromPdf.upload.extract')
          )}
        </Button>
      </div>
    </section>
  )
}
